import {
  Add as AddIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import CommissionRuleFormDialog from "../components/CommissionRuleFormDialog";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import {
  CALCULATION_TYPE_LABELS,
  PERCENT_BASE_LABELS,
  TRIGGER_EVENT_LABELS,
  getEnumLabel,
  isTieredCalculation,
} from "../constants/commissionEnums";

const formatAmount = (value) =>
  value == null || value === "" ? "—" : Number(value).toLocaleString("fa-IR");

const displayRuleAmount = (row) => {
  if (isTieredCalculation(row.calculationType)) {
    const count = row.tiers?.length || 0;
    return count ? `${count} پله` : "—";
  }
  if (row.calculationType === "FixedAmount") return formatAmount(row.fixedAmount);
  if (row.calculationType === "Percentage") return `${formatAmount(row.percentValue)}%`;
  return "—";
};

const CommissionRulesPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const groupMap = useMemo(
    () =>
      groups.reduce((acc, group) => {
        acc[group.id] = group.title;
        return acc;
      }, {}),
    [groups]
  );

  const loadGroups = useCallback(async () => {
    const res = await MarketGrowthAdminApi.getGroups();
    const normalized = MarketGrowthAdminApi.normalizeList(
      res,
      "بارگذاری گروه‌ها ناموفق بود."
    );
    if (!normalized.error) {
      setGroups(Array.isArray(normalized.data) ? normalized.data : []);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = groupFilter
        ? await MarketGrowthAdminApi.getRulesByGroup(groupFilter)
        : await MarketGrowthAdminApi.getRules();
      const { error: err, data } = MarketGrowthAdminApi.normalizeList(
        res,
        "بارگذاری قوانین ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        return;
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [groupFilter]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    load();
  }, [load]);

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        groupTitle: groupMap[row.commissionGroupId] || String(row.commissionGroupId),
        triggerEventLabel: getEnumLabel(TRIGGER_EVENT_LABELS, row.triggerEvent),
        calculationTypeLabel: getEnumLabel(CALCULATION_TYPE_LABELS, row.calculationType),
        percentBaseLabel: getEnumLabel(PERCENT_BASE_LABELS, row.percentBase),
        amountLabel: displayRuleAmount(row),
        statusLabel: row.isActive ? "فعال" : "غیرفعال",
      })),
    [groupMap, items]
  );

  const headCells = useMemo(
    () => [
      { id: "title", label: "عنوان" },
      { id: "groupTitle", label: "گروه" },
      { id: "triggerEventLabel", label: "رویداد" },
      { id: "calculationTypeLabel", label: "نوع محاسبه" },
      { id: "percentBaseLabel", label: "مبنای درصد" },
      { id: "amountLabel", label: "مبلغ/درصد" },
      { id: "priority", label: "اولویت", type: "number" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
    ],
    []
  );

  const openCreate = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await MarketGrowthAdminApi.getRuleById(row.id);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری جزئیات قانون ناموفق بود."
      );
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      setEditingItem(normalized.data);
      setDialogOpen(true);
    } catch (e) {
      toast.error(e?.message || "خطا در بارگذاری جزئیات");
    }
  };

  const closeDialog = () => {
    if (!saving) {
      setDialogOpen(false);
      setEditingItem(null);
    }
  };

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = editingItem
        ? await MarketGrowthAdminApi.updateRule(editingItem.id, payload)
        : await MarketGrowthAdminApi.createRule(payload);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        editingItem ? "ویرایش قانون ناموفق بود." : "ایجاد قانون ناموفق بود."
      );
      if (normalized.error) {
        return { error: normalized.error };
      }
      toast.success(editingItem ? "قانون با موفقیت ویرایش شد." : "قانون با موفقیت ایجاد شد.");
      setDialogOpen(false);
      setEditingItem(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    setStatusUpdatingId(row.id);
    try {
      const res = await MarketGrowthAdminApi.patchRuleStatus(row.id, !row.isActive);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "تغییر وضعیت قانون ناموفق بود."
      );
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success(row.isActive ? "قانون غیرفعال شد." : "قانون فعال شد.");
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در تغییر وضعیت");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const tableActions = useMemo(
    () => [
      {
        icon: <EditIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: openEdit,
        color: "primary",
        tooltip: "ویرایش",
      },
      {
        icon: <PowerSettingsNewIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: toggleStatus,
        color: "secondary",
        tooltip: "تغییر وضعیت",
        hidden: (row) => statusUpdatingId === row.id,
      },
    ],
    [statusUpdatingId]
  );

  return (
    <MarketGrowthPageShell
      title="قوانین پورسانت"
      description="قوانین محاسبه پورسانت بر اساس رویداد و گروه."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            ایجاد قانون جدید
          </Button>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>فیلتر گروه</InputLabel>
            <Select
              value={groupFilter}
              label="فیلتر گروه"
              onChange={(e) => setGroupFilter(e.target.value)}
            >
              <MenuItem value="">همه گروه‌ها</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            defaultSort={{ orderBy: "priority", order: "asc" }}
            tableMinWidth={1100}
          />
        </CardContent>
      </Card>

      <CommissionRuleFormDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        initialData={editingItem}
        groups={groups}
        saving={saving}
      />
    </MarketGrowthPageShell>
  );
};

export default CommissionRulesPage;
